import { html, render, nothing } from 'lit';
import { virtual, useEffect, useState, useMemo } from 'haunted';
import { mediaTmpl } from './_lib/media-tmpl.js';
import linkifyHtml from 'linkify-html';
import { escapeHtml, sanitizeHtml } from '@local/sanitize-html';
import { Loading } from '../loading/index.js';
import { ButtonMoreContent } from '@local/templates/button-more-content';
import { handleModal } from '@local/utils-handle-modal';
import { baseRoute, locale } from '@local/config';
import { getI18n } from './_i18n/i18n.js';

export function formatPlainText(text) {
  const newText = escapeHtml(String(text ?? '').replaceAll('\n\n', '\n'));
  return sanitizeHtml(
    linkifyHtml(
      String.raw`<p class="mt-2 text-base">
      ${newText.split('\n').join('</p><p class="mt-2 text-base ">')}
    </p>`,
      {
        defaultProtocol: 'https',
        target: '_blank',
        rel: 'noreferrer noopener',
        className: 'link link--generated',
        attributes: {
          title: 'Opens in new window/tab'
        }
      }
    )
  );
}

const ExternalMediaView = virtual(() => {
  const [currentItems, setCurrentItems] = useState([]);
  const [nextPageToken, setNextPageToken] = useState([]);
  const [moreContentLoading, setMoreContentLoading] = useState(false);

  const currentLocale = locale();

  const literals = useMemo(
    () => getI18n(currentLocale) ?? getI18n('en'),
    [currentLocale]
  );

  useEffect(() => {
    fetch(`/q/external-api/yt`)
      .then(response => response.json())
      .then(data => {
        setCurrentItems(currentItems.concat(data.items));
        setNextPageToken(data.nextPageToken);
      });
  }, []);

  function handleMoreVideos() {
    setMoreContentLoading(true);
    fetch(`/q/external-api/yt/page/${nextPageToken}`)
      .then(response => response.json())
      .then(data => {
        const firstId = data.items[0].snippet.resourceId.videoId;
        const newCurrentItems = currentItems.concat(data.items);
        setCurrentItems(newCurrentItems);
        setNextPageToken(data.nextPageToken);
        setMoreContentLoading(false);
        setTimeout(() => {
          document.getElementById(`media-button-${firstId}`).focus();
        }, 100);
      });
  }

  return currentItems.length
    ? html`
        <ul
          class="gallery-items grid grid-cols-1 s:grid-cols-2 l:grid-cols-3 gap-4"
        >
          ${currentItems.map(item => {
            const { title, resourceId, description = null } = item.snippet;
            const fnHandleModal = handleModal({
              view: mediaTmpl,
              title,
              params: {
                description: description ? formatPlainText(description) : null,
                title: title || 'YouTube video',
                videoId: resourceId.videoId,
                messages: {},
                baseRoute
              }
            });

            return html`
              <li class="flex flex-col">
                <div
                  class="gallery-items__item-button p-2 cursor-pointer"
                  role="button"
                  tabindex="0"
                  id="media-button-${resourceId.videoId}"
                  @click="${fnHandleModal}"
                  @keydown="${fnHandleModal}"
                >
                  <div class="w-full aspect-video relative overflow-hidden">
                    <img
                      src="/q/yt-image/${resourceId.videoId}"
                      alt=""
                      class="absolute top-1/2 left-0 w-full -translate-y-1/2"
                    />
                  </div>
                  <div class="gallery-items__item-title mt-2 text-base">
                    ${title}
                  </div>
                </div>
              </li>
            `;
          })}
        </ul>
        ${
          moreContentLoading
            ? Loading()
            : nextPageToken
              ? ButtonMoreContent({
                  text: literals.moreVideos,
                  eventFn: handleMoreVideos
                })
              : nothing
        }
      `
    : Loading();
});

export function ExternalMedia() {
  const element = document.getElementById('gallery-video');
  if (element) {
    render(ExternalMediaView(), element);
  }
}
